import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import { useAuthStore } from '../store/authStore';

interface SharePhotoModalProps {
  photoId: string;
  onClose: () => void;
}

interface User {
  userId: string;
  name: string;
  email: string;
}

export default function SharePhotoModal({ photoId, onClose }: SharePhotoModalProps) {
  const { user: currentUser } = useAuthStore();
  const queryClient = useQueryClient();
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [message, setMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  // Fetch active users
  const { data: usersData, isLoading: usersLoading } = useQuery<{ users: User[] }, Error>({
    queryKey: ['activeUsers'],
    queryFn: api.listActiveUsers,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Filter out current user (can't share with yourself)
  const users = (usersData?.users || []).filter(u => u.userId !== currentUser?.userId);
  
  // Share photo mutation
  const sharePhotoMutation = useMutation({
    mutationFn: (data: { photoId: string; userIds: string[]; message?: string }) =>
      api.sharePhoto(data),
    onSuccess: () => {
      setShowSuccess(true);
      // Invalidate notifications to show the new ones
      queryClient.invalidateQueries({ queryKey: ['notifications-recent'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
      
      // Close modal after 1.5 seconds
      setTimeout(() => {
        onClose();
      }, 1500);
    },
    onError: (error) => {
      console.error('❌ Share photo error:', error);
    },
  });

  const handleToggleUser = (userId: string) => {
    setSelectedUserIds(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleShare = () => {
    if (selectedUserIds.length === 0) return;

    sharePhotoMutation.mutate({
      photoId,
      userIds: selectedUserIds,
      message: message.trim() || undefined,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Share Photo</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={sharePhotoMutation.isPending}
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Success message */}
        {showSuccess && (
          <div className="px-6 py-4 bg-green-50 border-b border-green-200">
            <div className="flex items-center gap-2 text-green-800">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="font-medium">Photo shared successfully!</span>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* User selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Select people to share with:
            </label>
            
            {usersLoading ? (
              <div className="text-center py-8 text-gray-500">Loading users...</div>
            ) : users.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No other users available</div>
            ) : (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {users.map((user) => (
                  <label
                    key={user.userId}
                    className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all cursor-pointer hover:bg-gray-50 ${
                      selectedUserIds.includes(user.userId)
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedUserIds.includes(user.userId)}
                      onChange={() => handleToggleUser(user.userId)}
                      className="w-5 h-5 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      disabled={sharePhotoMutation.isPending}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 truncate">{user.name}</div>
                      <div className="text-xs text-gray-500 truncate">{user.email}</div>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Optional message */}
          <div className="mb-4">
            <label htmlFor="share-message" className="block text-sm font-medium text-gray-700 mb-2">
              Optional message:
            </label>
            <textarea
              id="share-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Check this out!"
              maxLength={200}
              rows={3}
              disabled={sharePhotoMutation.isPending}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
            <div className="text-xs text-gray-500 text-right mt-1">
              {message.length} / 200
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
          <button
            onClick={onClose}
            disabled={sharePhotoMutation.isPending}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleShare}
            disabled={sharePhotoMutation.isPending || selectedUserIds.length === 0}
            className="px-4 py-2 bg-gradient-to-r from-rose-500 via-fuchsia-500 to-indigo-500 text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sharePhotoMutation.isPending ? (
              <>
                <svg className="inline w-4 h-4 mr-2 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Sharing...
              </>
            ) : (
              <>
                Share {selectedUserIds.length > 0 && `(${selectedUserIds.length})`}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

